import React, { useCallback, useEffect, useState } from "react";
import { AccountTree, ArrowForward, Campaign } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { Link as RouterLink, Navigate } from "react-router-dom";
import { useApiClient } from "../../../api";
import { FruitifyContainer } from "../../../shared-components/Containers";
import { useAuthenticationStore } from "../../../stores/auth";
import SenderProfilesSection from "../onboarding/SenderProfilesSection";

export default function MarketingPage() {
  const api = useApiClient().marketing;
  const user = useAuthenticationStore((state) => state.user);
  const isSuperuser = user?.info?.activeContext?.contextType === "superuser";
  const [workflows, setWorkflows] = useState([]);
  const [senders, setSenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await api.getManagement();
      setWorkflows(result.marketingWorkflows || []);
      setSenders(result.marketingSenders || []);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  if (!isSuperuser) return <Navigate to="/" replace />;

  return (
    <FruitifyContainer>
      <Stack spacing={4}>
        <Box>
          <Typography variant="h4">Marketing and Customer Workflows</Typography>
          <Typography color="text.secondary">
            Manage marketing outreach, automated user journeys and the sending
            profiles used to communicate with recipients.
          </Typography>
        </Box>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Button
            variant="outlined"
            component={RouterLink}
            to="/superuser/marketing/announcements"
          >
            Announcements
          </Button>
          <Button
            variant="outlined"
            component={RouterLink}
            to="/superuser/marketing/segments"
          >
            Segments
          </Button>
        </Stack>
        {error && <Alert severity="error">{error}</Alert>}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardContent sx={{ height: "100%", boxSizing: "border-box" }}>
                <Stack spacing={2} height="100%">
                  <AccountTree color="primary" sx={{ fontSize: 38 }} />
                  <Typography variant="h5">Customer workflows</Typography>
                  <Typography color="text.secondary" flex={1}>
                    Create and manage automated journeys for active Fruitify
                    users.
                  </Typography>
                  <Button
                    component={RouterLink}
                    to="/superuser/marketing/workflows"
                    endIcon={<ArrowForward />}
                  >
                    Manage workflows
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardContent sx={{ height: "100%", boxSizing: "border-box" }}>
                <Stack spacing={2} height="100%">
                  <Campaign color="primary" sx={{ fontSize: 38 }} />
                  <Typography variant="h5">Outreach</Typography>
                  <Typography color="text.secondary" flex={1}>
                    Find and review prospective contacts, and manage journeys
                    intended to attract new customers.
                  </Typography>
                  <Button
                    component={RouterLink}
                    to="/superuser/marketing/outreach"
                    endIcon={<ArrowForward />}
                  >
                    Open outreach
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        {loading ? (
          <Box textAlign="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <SenderProfilesSection
            api={api}
            senders={senders}
            workflows={workflows}
            onChanged={load}
          />
        )}
      </Stack>
    </FruitifyContainer>
  );
}
